import DOMPurify from 'dompurify';

export const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const SAFE_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

const API_ERROR_HTML_CONFIG = {
  ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 's', 'code', 'small', 'p', 'ul', 'ol', 'li', 'br'],
  ALLOWED_ATTR: ['type'],
};

const RICH_TEXT_HTML_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'div',
    'span',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    'pre',
    'code',
    'a',
  ],
  ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'rel', 'type', 'data-list'],
  ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
};

/** Sanitize API error HTML — structural/formatting tags only. */
export const toSafeApiErrorHtml = html => DOMPurify.sanitize(html ?? '', API_ERROR_HTML_CONFIG);

/** Sanitize rich text HTML (from Quill/Jodit editors). */
export const sanitizeRichTextHtml = html => DOMPurify.sanitize(html ?? '', RICH_TEXT_HTML_CONFIG);

/** True when value looks like real rich-text HTML (not plain text with angle brackets). */
export const looksLikeHtml = value => {
  const raw = String(value ?? '');
  if (!raw) return false;
  // Only treat as HTML when we see known rich-text tags Quill/Jodit emit.
  // Avoid matching plain text like "balance <escrow>".
  return /<\/?(?:p|br|div|span|strong|b|em|i|u|s|strike|h[1-6]|ul|ol|li|blockquote|pre|code|a)\b/i.test(
    raw
  );
};

/**
 * Render note/task rich text for display.
 * HTML is sanitized; legacy plain text is escaped and newlines become <br>.
 */
export const toDisplayableRichTextHtml = value => {
  const raw = value ?? '';
  if (!raw) return '';
  if (looksLikeHtml(raw)) return sanitizeRichTextHtml(raw);
  return escapeHtml(raw).replace(/\r\n|\r|\n/g, '<br>');
};

/**
 * Coerce legacy plain text into Quill-friendly HTML when loading an editor value.
 * Existing HTML is sanitized before handing it to the editor.
 */
export const toRichTextEditorValue = value => {
  const raw = value ?? '';
  if (!raw) return '';
  if (looksLikeHtml(raw)) return sanitizeRichTextHtml(raw);
  return String(raw)
    .split(/\r\n|\r|\n/)
    .map(line => `<p>${escapeHtml(line) || '<br>'}</p>`)
    .join('');
};

/** Strip rich-text HTML back to plain text (preserves paragraph/line breaks). */
export const toPlainTextFromRichText = value => {
  const raw = String(value ?? '');
  if (!raw) return '';
  if (!looksLikeHtml(raw)) return raw;
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<\/?p[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
